import { useEffect } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { liveResultCache } from '../services/liveResultCache';

/**
 * useLiveQuery with a warm start. Dexie's useLiveQuery has NO cache: it returns
 * undefined on the first render of every mount, so every navigation to a
 * liveQuery-driven screen paints a skeleton/empty frame even when the exact same
 * data was on screen a second earlier (warm IndexedDB reads resolve in 10-80ms -
 * a 1-3 frame flash that reads as flicker, not loading).
 *
 * This wrapper seeds dexie's defaultResult with the last result seen for cacheKey,
 * so remounts render real data on the FIRST frame; the live emission then replaces
 * it (usually with identical data - no visual change). Dexie reactivity is fully
 * preserved - this only changes what renders before the first emission.
 */
export function useLiveQueryCached<T>(
  cacheKey: string,
  querier: () => Promise<T> | T,
  deps: unknown[] = [],
): T | undefined {
  const seeded = liveResultCache.get(cacheKey) as T | undefined;
  const result = useLiveQuery(querier, deps, seeded);

  useEffect(() => {
    if (result !== undefined) {
      liveResultCache.set(cacheKey, result);
    }
  }, [cacheKey, result]);

  return result;
}
